import {produce} from 'immer';
import {useSetAtom} from 'jotai';
import {useState} from 'react';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {Button} from '@/components/Button';
import {HighlightText} from '@/components/HighlightText';
import toast from '@/components/HotToast';
import {SearchIcon} from '@/components/Icons';
import {LoadingBar} from '@/components/LoadingBar';
import {
  SidePanelBase, SidePanelFilterRow, SidePanelFilterSection, SidePanelHead,
  SidePanelHeadTitleSection, SidePanelHeadTitleText, SidePanelScrollBody
} from '@/components/SidePanel/StyledComponents';
import {TextField} from '@/components/TextField';
import {Typography} from '@/components/Typography';
import {FranchiseItem} from '@/map/@types';
import {
  useAddStarredBrands, useRemoveStarredBrands, useSearchFranchiseByKeyword, useStarredBrands
} from '@/map/api';
import {starredBrandsInfoAtom} from '@/map/atom';
import {Flexbox, FlexCenter, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {Logo} from './Logo';

interface IProps {
  limitedBrandCount: number;
  onBackClick?(): void;
}

export const Edit = ({limitedBrandCount, onBackClick}: IProps) => {
  const [keyword, setKeyword] = useState<string>('');

  const {data: searchResult, isLoading} = useSearchFranchiseByKeyword(keyword);
  const {data: starredBrands} = useStarredBrands();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const brandList = keyword?.length ? searchResult : starredBrands;

  return (
    <Root>
      <SidePanelHead>
        <SidePanelHeadTitleSection>
          <SidePanelHeadTitleText>관심브랜드 편집</SidePanelHeadTitleText>
          <FlexFullGrow />
        </SidePanelHeadTitleSection>
        <SidePanelFilterSection>
          <SidePanelFilterRow>
            <TextField
              icon={SearchIcon}
              variant="medium"
              type="text"
              name="search-text"
              className="search-input"
              placeholder="브랜드 명"
              value={keyword}
              onChange={handleChange}
              onClear={() => setKeyword('')}
            />
          </SidePanelFilterRow>
        </SidePanelFilterSection>
        <ItemCounter>
          {!!keyword.length
            ? `검색 결과 ${brandList?.length ?? 0}`
            : `관심 브랜드(${starredBrands?.length ?? 0}/${limitedBrandCount})`}
        </ItemCounter>
      </SidePanelHead>
      <SidePanelScrollBody>
        {isLoading ? (
          <FlexCenter style={{height: '100%'}}>
            <LoadingBar size={24} borderSize={2} color={palette.black} />
          </FlexCenter>
        ) : !!brandList && brandList?.length > 0 ? (
          brandList.map((brand) => (
            <BrandItem
              key={brand.id}
              brand={brand}
              keyword={keyword}
              maxCount={limitedBrandCount}
              starredCount={starredBrands?.length}
              active={
                !!starredBrands &&
                starredBrands?.findIndex((i) => i.id === brand.id) >= 0
              }
            />
          ))
        ) : (
          <PaddingBox spacing={16} style={{textAlign: 'center'}}>
            <Typography variant="medium14">검색 결과가 없습니다.</Typography>
          </PaddingBox>
        )}
      </SidePanelScrollBody>
      <PaddingBox spacing={[8, 16]}>
        <Button
          variant="fill"
          color="secondary"
          size="medium"
          fullWidth
          onClick={() => onBackClick?.()}
        >
          완료
        </Button>
      </PaddingBox>
    </Root>
  );
};

const BrandItem = ({
  brand,
  active,
  keyword,
  maxCount,
  starredCount,
}: {
  brand: FranchiseItem;
  active: boolean;
  keyword: string;
  maxCount?: number;
  starredCount?: number;
}) => {
  const {mutate: remove, isPending: isRemoveLoading} = useRemoveStarredBrands();
  const {mutate: update, isPending: isUpdateLoading} = useAddStarredBrands();

  const setSelectedInfo = useSetAtom(starredBrandsInfoAtom);

  const addBrand = (id: string) => {
    if (starredCount && maxCount && maxCount <= starredCount) {
      return toast.error(
        `관심 브랜드는 최대 ${maxCount}개까지 추가할 수 있습니다.`
      );
    }

    update([id], {
      onError(error) {
        toast.error(getGqlErrorMessage(error));
      },
    });

    setSelectedInfo(
      produce((draft) => {
        draft.selected.push(id);
      })
    );
  };

  const removeBrand = (id: string) => {
    remove([id], {
      onError(error) {
        toast.error(getGqlErrorMessage(error));
      },
    });

    setSelectedInfo(
      produce((draft) => {
        const idx = draft.selected.findIndex((i: string) => i === id);

        if (idx >= 0) {
          draft.selected.splice(idx, 1);
        }
      })
    );
  };

  return (
    <Item key={brand.id}>
      <Logo item={brand} />
      <Typography variant="medium14" ellipsis>
        <HighlightText
          query={keyword}
          text={brand.overriden?.name ?? brand.name}
          highlightColor={palette.blue100}
        />
      </Typography>
      <FlexFullGrow />
      {active ? (
        <Button
          variant="ghost"
          size="small"
          color="red"
          noPadding
          disabled={isRemoveLoading}
          onClick={() => removeBrand(brand.id)}
        >
          {isRemoveLoading ? '제거 중...' : '제거'}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="small"
          color="black"
          noPadding
          disabled={isUpdateLoading}
          onClick={() => addBrand(brand.id)}
        >
          {isUpdateLoading ? '추가 중...' : '추가'}
        </Button>
      )}
    </Item>
  );
};

const Root = styled(SidePanelBase)`
  font-weight: 500;
  color: ${palette.textPrimary};
`;

const ItemCounter = styled(Typography).attrs({
  variant: 'medium12',
  as: 'div',
})`
  padding: 8px 16px;
  color: ${palette.textSecondary};
`;

const Item = styled(Flexbox)`
  width: 100%;
  padding: 11px 16px;
  gap: 4px;
  & > * {
    flex-shrink: 0;
  }
`;
